import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useChowaTheme } from './chowaThemeContext';

const ChowaTable = ({ columns, data, renderMobileRow, tableColor }) => {
  const { isDark, currentThemeName } = useChowaTheme();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // 画面幅に応じた表示制御
  const isSmallScreen = windowWidth < 1200;
  const isLargeScreen = windowWidth >= 1800;
  const isMediumLargeScreen = windowWidth >= 1600;
  const isVeryLargeScreen = windowWidth >= 1980;
  const isExtraLargeScreen = windowWidth >= 2400;
  const isVerySmallScreen = windowWidth < 1000;
  const isExtraSmallScreen = windowWidth < 800;
  const isMobileView = windowWidth <= 888; // 888px以下はモバイル表示

  // 全列を常に表示
  const getVisibleColumns = () => columns;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const colorStyles = {
    blue: {
      border: { light: 'border-blue-500', dark: 'border-blue-400' },
      headerBg: { light: 'bg-blue-100', dark: 'dark:bg-blue-900/50' },
      divider: { light: 'divide-blue-200', dark: 'dark:divide-blue-800' }
    },
    orange: {
      border: { light: 'border-orange-500', dark: 'border-orange-400' },
      headerBg: { light: 'bg-orange-100', dark: 'dark:bg-orange-900/50' },
      divider: { light: 'divide-orange-200', dark: 'dark:divide-orange-800' }
    },
    purple: {
      border: { light: 'border-purple-500', dark: 'border-purple-400' },
      headerBg: { light: 'bg-purple-100', dark: 'dark:bg-purple-900/50' },
      divider: { light: 'divide-purple-200', dark: 'dark:divide-purple-800' }
    },
    gray: {
      border: { light: 'border-gray-500', dark: 'border-gray-400' },
      headerBg: { light: 'bg-gray-100', dark: 'dark:bg-gray-900/50' },
      divider: { light: 'divide-gray-200', dark: 'dark:divide-gray-800' }
    },
  };

  const themeMode = isDark ? 'dark' : 'light';
  const selectedColor = colorStyles[tableColor || currentThemeName] ?? colorStyles.gray;

  const borderColorClass = selectedColor.border?.[themeMode] ?? 'border-gray-200 dark:border-gray-700';
  const headerBgClass = selectedColor.headerBg?.[themeMode] ?? 'bg-gray-100 dark:bg-gray-700/50';
  const dividerColorClass = selectedColor.divider?.[themeMode] ?? 'divide-gray-200 dark:divide-gray-700';

  const defaultMobileRow = (item) => (
    <div className="space-y-1 text-sm">
      {getVisibleColumns().map(col => (
        <div key={col.accessor} className="flex justify-between">
          <span className="font-semibold text-gray-800 dark:text-gray-100">{col.header}:</span>
          <span className="text-gray-600 dark:text-gray-300">{col.cell ? col.cell(item) : item[col.accessor]}</span>
        </div>
      ))}
    </div>
  );

  // レスポンシブ対応の列幅計算（画面幅に応じて可変）
  const getColumnWidth = (colHeader) => {
    // 画面幅に応じた基本スケール係数
    let scaleFactor = 1;
    if (isExtraSmallScreen) scaleFactor = 0.8;
    else if (isVerySmallScreen) scaleFactor = 0.9;
    else if (isSmallScreen) scaleFactor = 1.0;
    else if (isMediumLargeScreen) scaleFactor = 1.3;
    else if (isLargeScreen) scaleFactor = 1.6;
    else if (isVeryLargeScreen) scaleFactor = 2.0;
    else if (isExtraLargeScreen) scaleFactor = 4.0;
    else scaleFactor = 1.1;

    // 最小幅の設定（これより小さくならない）
    const getMinWidth = (base) => `${Math.round(base * scaleFactor)}px`;

    // 列の相対幅の設定
    if (colHeader === 'CustomUnitName') {
      return `minmax(${getMinWidth(120)}, 3fr)`; // 最も広く
    } else if (colHeader === 'Unit Serial') {
      return `minmax(${getMinWidth(80)}, 2fr)`; // 中程度
    } else if (colHeader === 'Device ID') {
      return `minmax(${getMinWidth(80)}, 2fr)`; // 中程度
    } else if (colHeader === 'Last Update') {
      return `minmax(${getMinWidth(100)}, 2.5fr)`; // やや広め
    } else if (colHeader === 'Operating Time') {
      return `minmax(${getMinWidth(85)}, 2fr)`; // 中程度
    } else {
      return `minmax(${getMinWidth(70)}, 1.5fr)`; // デフォルト
    }
  };

  // レスポンシブ対応のフォントサイズ
  const getResponsiveFontSize = () => {
    if (isExtraSmallScreen) return '0.65rem';
    if (isVerySmallScreen) return '0.7rem';
    if (isSmallScreen) return '0.75rem';
    if (isMediumLargeScreen) return '0.85rem'; // 1600-1800px
    if (isLargeScreen) return '0.9rem'; // 1800-1980px
    if (isVeryLargeScreen) return '0.95rem'; // 1980-2400px
    if (isExtraLargeScreen) return '1.2rem'; // 2400px以上（4K対応）- より大きく
    return '0.8rem';
  };

  // レスポンシブ対応のパディング
  const getResponsivePadding = () => {
    if (isExtraSmallScreen) return '0.2rem 0.1rem';
    if (isVerySmallScreen) return '0.3rem 0.2rem';
    if (isSmallScreen) return '0.4rem 0.3rem';
    if (isMediumLargeScreen) return '0.5rem 0.4rem'; // 1600-1800px
    if (isLargeScreen) return '0.6rem 0.5rem'; // 1800-1980px
    if (isVeryLargeScreen) return '0.7rem 0.6rem'; // 1980-2400px
    if (isExtraLargeScreen) return '1.2rem 1.0rem'; // 2400px以上（4K対応）- より大きく
    return '0.45rem 0.35rem';
  };

  // レスポンシブ対応のギャップ
  const getResponsiveGap = () => {
    if (isExtraSmallScreen) return 'gap-0.5';
    if (isVerySmallScreen) return 'gap-1';
    if (isSmallScreen) return 'gap-2';
    if (isMediumLargeScreen) return 'gap-3'; // 1600-1800px
    if (isLargeScreen) return 'gap-4'; // 1800-1980px
    if (isVeryLargeScreen) return 'gap-5'; // 1980-2400px
    if (isExtraLargeScreen) return 'gap-8'; // 2400px以上（4K対応）- より大きく
    return 'gap-2.5';
  };

  // レスポンシブ対応のコンテナパディング
  const getResponsiveContainerPadding = () => {
    if (isExtraSmallScreen) return 'px-1';
    if (isVerySmallScreen) return 'px-1.5';
    if (isSmallScreen) return 'px-2';
    if (isMediumLargeScreen) return 'px-3'; // 1600-1800px
    if (isLargeScreen) return 'px-4'; // 1800-1980px
    if (isVeryLargeScreen) return 'px-5'; // 1980-2400px
    if (isExtraLargeScreen) return 'px-8'; // 2400px以上（4K対応）- より大きく
    return 'px-2.5';
  };

  return (
    <div 
      className={classNames(
        "w-full bg-white dark:bg-gray-800 rounded-lg border overflow-hidden h-full flex flex-col",
        borderColorClass
      )}
      style={{ borderWidth: '2px' }}
    >
      {/* --- テーブルヘッダー (md以上で表示、888px以下では非表示) --- */}
      <div 
        className={classNames(
          `${isMobileView ? 'hidden' : 'hidden md:grid'} flex-shrink-0 py-2 ${getResponsiveGap()} ${getResponsiveContainerPadding()}`,
          headerBgClass
        )} 
        style={{ 
          gridTemplateColumns: getVisibleColumns().map(col => getColumnWidth(col.header)).join(' ')
        }}
      >
        {getVisibleColumns().map(col => (
          <p key={col.accessor} className="font-semibold text-gray-800 dark:text-gray-100 text-center" style={{ 
            fontSize: getResponsiveFontSize(),
            whiteSpace: 'normal',
            lineHeight: '1.2',
            wordBreak: 'break-word',
            minHeight: isExtraSmallScreen ? '2rem' : '2.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: getResponsivePadding()
          }}>
            {col.header}
          </p>
        ))}
      </div>

      {/* --- データエリア --- */}
      <div className={classNames(
        "divide-y flex-grow flex flex-col",
        dividerColorClass
      )}>
        {data.map((item, index) => (
          <div key={index} className={`py-2 flex-grow flex items-center ${getResponsiveContainerPadding()} ${isExtraSmallScreen ? 'py-1' : ''}`}>
            {/* --- PC/タブレット表示 (md以上、888px以下では非表示) --- */}
            <div className={`${isMobileView ? 'hidden' : 'hidden md:grid'} items-center text-gray-600 dark:text-gray-300 w-full ${getResponsiveGap()}`} style={{ 
              gridTemplateColumns: getVisibleColumns().map(col => getColumnWidth(col.header)).join(' ')
            }}>
              {getVisibleColumns().map(col => (
                <div key={col.accessor} className="text-center" style={{
                  fontSize: getResponsiveFontSize(),
                  lineHeight: '1.3',
                  padding: getResponsivePadding(),
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word'
                }}>
                  {col.cell ? col.cell(item) : item[col.accessor]}
                </div>
              ))}
            </div>

            {/* --- モバイル表示 (md未満 または 888px以下) --- */}
            <div className={`${isMobileView ? 'block' : 'md:hidden'} w-full`}>
              {renderMobileRow ? renderMobileRow(item) : defaultMobileRow(item)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChowaTable;
